/**
 * shared/space_stations.ts — CLIENT MIRROR of server/src/lib/space_stations.ts
 * (the server copy is authoritative; keep them byte-identical below the
 * headers). The station REGISTRY (Space Wave 1): which stations exist, where
 * they sit, how close "docked" is; the per-system arrival point; the wormhole
 * throat seat; the range constants the server enforces. Pure.
 */
import { systemSector, type StarSystem } from './galaxy';

export interface StationSpec {
  key: string;            // '<systemId>:st<n>'
  systemId: string;
  name: string;
  pos: [number, number, number];
  radius: number;
  /** Within this distance of `pos` a pilot may dock (radius + 800). */
  dockRange: number;
}

/** The Commons Reach's five stations, in space_sector.ts POI-table order
 *  (verify:space-stations pins the duplication against POIS). */
export const SYS0_STATIONS: readonly StationSpec[] = [
  { key: 'sys0:st0', systemId: 'sys0', name: 'Waystation Aurel', pos: [380_000, 26_000, 140_000], radius: 2_600, dockRange: 3_400 },
  { key: 'sys0:st1', systemId: 'sys0', name: 'Nova Aurel Station', pos: [1_050_000, 240_000, -780_000], radius: 2_900, dockRange: 3_700 },
  { key: 'sys0:st2', systemId: 'sys0', name: 'Aurel Prime', pos: [9_500, 900, 5_200], radius: 5_200, dockRange: 6_000 },
  { key: 'sys0:st3', systemId: 'sys0', name: 'Farlight Terminal', pos: [-800_000, -140_000, -900_000], radius: 2_700, dockRange: 3_500 },
  { key: 'sys0:st4', systemId: 'sys0', name: 'Kessway Depot', pos: [520_000, 180_000, 1_250_000], radius: 2_400, dockRange: 3_200 },
];
export const ALT_STATION_RADIUS_M = 1_800;
export const STATION_KEY_RE = /^(sys\d{1,3}|w\d{1,20}):st\d{1,3}$/;

/** Every station of a system: five in the Commons Reach, one Waypoint in a
 *  prebuilt system that hashes a station, none in a user-world system. */
export function stationsOf(sys: StarSystem): StationSpec[] {
  if (sys.id === 'sys0') return [...SYS0_STATIONS];
  if (sys.worldId || !sys.hasStation) return [];
  const st = systemSector(sys).station;
  if (!st) return [];
  return [{ key: `${sys.id}:st0`, systemId: sys.id, name: `${sys.name} Waypoint`, pos: [st[0], st[1], st[2]], radius: ALT_STATION_RADIUS_M, dockRange: ALT_STATION_RADIUS_M + 800 }];
}

export function stationByKey(systems: readonly StarSystem[], key: string): StationSpec | null {
  if (!STATION_KEY_RE.test(key)) return null;
  const sysId = key.split(':')[0];
  const sys = systems.find((s) => s.id === sysId);
  if (!sys) return null;
  return stationsOf(sys).find((s) => s.key === key) ?? null;
}

/** Where a pilot arrives in a system: 2.4 star radii out on +X, or 2.4 planet
 *  radii off a user world's planet (the client's activate() formula). */
export function systemArrival(sys: StarSystem, wp: { x: number; y: number; z: number; radius: number } | null): [number, number, number] {
  if (sys.worldId && wp) return [wp.x + wp.radius * 2.4, wp.y, wp.z];
  return [systemSector(sys).star.radius * 2.4, 0, 0];
}

/** Bare FNV-1a in [0,1) — SpaceModule's `warH`, the wormhole seat hash. */
export function warH(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 4294967296;
}
/** The box a throat's seat lies in (±x, ±y, ±z metres); the seat itself is SERVER-ONLY
 *  (space_sig — THE HIDDEN-POSITION LAW): a pilot learns it by scanning, never by hashing. */
export const WH_THROAT_BOX_M: readonly [number, number, number] = [130_000, 35_000, 130_000];

/** Ranges the server enforces from the streamed position. */
export const WH_JUMP_RANGE_M = 700;
export const TACKLE_RANGE_M = 6_000;
export const SIEGE_RANGE_M = 8_000;
export const DEPLOY_RANGE_M = 6_000;
/** THE PVP LAW: a blow on another pilot lands only under a live wardec between their guilds,
 *  both in flight in one system, from within this of the target (the streamed positions). */
export const PVP_RANGE_M = 2_500;
export const PVP_HIT_MAX = 12;
export const PVP_HIT_TTL_S = 60;
/** THE CREW LAW: a pilot takes a friend's Sovereign turret seat from within this of the hull,
 *  both in flight in one system; the seat is lost when either docks, jumps or falls silent. */
export const CREW_BOARD_RANGE_M = 3_000;
export const CREW_STALE_S = 120;
/** The dorsal turret's seat above the Sovereign's spine (hull frame, metres). */
export const CREW_TURRET_SEAT: readonly [number, number, number] = [0, 6, -2];
/** The hyper-warp crossing (seconds) and how late a client may complete it
 *  before the sweep does. */
export const SYSJUMP_S = 30;
export const HYPER_GRACE_S = 120;
